import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container, 
  Alert, 
  InputAdornment, 
  IconButton, 
  Link, 
  CircularProgress, 
  FormControlLabel, 
  Checkbox,
  Paper,
  Fade
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Login as LoginIcon,
  PersonOutline,
  LockOutlined
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import backgroundImage from '../assets/images/sign_in.jpg';

// Styled components for modern design
const GradientBox = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%), url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  padding: theme.spacing(2),
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  backdropFilter: 'blur(10px)',
  backgroundColor: alpha(theme.palette.background.paper, 0.85),
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  width: '100%',
  maxWidth: 420,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    backgroundColor: alpha(theme.palette.common.white, 0.9),
    '& fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.3),
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    },
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5),
  fontWeight: 'bold',
  boxShadow: '0 4px 15px 0 rgba(116, 79, 168, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px 0 rgba(116, 79, 168, 0.4)',
  },
}));

const EneuronLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // Load email from localStorage if rememberMe is true
  useEffect(() => {
    if (localStorage.getItem('rememberMe') === 'true') {
      setEmail(localStorage.getItem('email') || '');
      setRememberMe(true);
    }
  }, []);

  // Handle form submission
  // In your handleSubmit function, update the API calls:

const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');

  if (!email || !password) {
    setError('Both email and password are required.');
    return;
  }

  if (!validateEmail(email)) {
    setError('Please enter a valid email.');
    return;
  }

  setLoading(true);

  try {
    console.log('Attempting login...');

    // Login API call - using the simplified endpoint
    const loginResponse = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/eneurons/login.php`,
      { 
        email: email.trim(), 
        password: password 
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 15000,
      }
    );

    console.log('Login response:', loginResponse.data);

    if (loginResponse.data.access_token) {
      const authToken = loginResponse.data.access_token;
      localStorage.setItem('authToken', authToken);
      
      // Store user data directly from login response
      const userProfile = loginResponse.data.user;
      localStorage.setItem('userProfile', JSON.stringify(userProfile));
      
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('email', email);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('email');
      }

      // Redirect to dashboard
      navigate('/dashboard_e-neurons');
      setError('');
    } else {
      setError('Invalid response from server.');
    }
  } catch (err) {
    console.error('Login error:', err);
    
    if (err.response && err.response.data) {
      setError(err.response.data.error || 'Invalid credentials.');
    } else if (err.code === 'ECONNABORTED') {
      setError('Request timeout. Please try again.');
    } else if (err.request) {
      setError('Network error. Please check your connection.');
    } else {
      setError('An error occurred. Please try again.');
    }
  } finally {
    setLoading(false);
  }
};
  // Validate email format
  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <GradientBox>
      <Container maxWidth="sm">
        <Fade in={true} timeout={800}>
          <Box display="flex" justifyContent="center">
            <GlassPaper elevation={24}>
              <Box textAlign="center" mb={3}>
                <Typography 
                  variant="h4" 
                  color="primary" 
                  fontWeight="700" 
                  gutterBottom
                  sx={{
                    background: 'linear-gradient(45deg, #1976d2, #7b1fa2)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  e-Neuron Hotel Admin
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Sign in to your account
                </Typography>
              </Box>

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2, 
                    borderRadius: 2,
                    alignItems: 'center'
                  }}
                >
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <StyledTextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleTogglePassword} 
                          edge="end" 
                          aria-label="Toggle password visibility"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Remember Me
                      </Typography>
                    }
                  />
                  
                  <Link 
                    href="#" 
                    variant="body2" 
                    underline="hover" 
                    color="primary"
                    sx={{ fontWeight: 500 }}
                  >
                    Forgot Password?
                  </Link>
                </Box>

                <GradientButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !email || !password}
                  startIcon={loading ? null : <LoginIcon />}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    'Sign In'
                  )}
                </GradientButton>
              </form>

              <Box mt={3} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  Don't have an account?{' '}
                  <Link 
                    href="#" 
                    underline="hover" 
                    color="primary" 
                    fontWeight="500"
                    onClick={() => navigate('/register')}
                  >
                    Contact Administrator
                  </Link>
                </Typography>
              </Box>
            </GlassPaper>
          </Box>
        </Fade>
      </Container>
    </GradientBox>
  );
};

export default EneuronLogin;